/**
 * The build this bundle is, and whether the server is serving a different one.
 *
 * `__CRM_VERSION__` is replaced at bundle time by build.mjs from `$(CrmFullVersion)` - the same
 * number `Directory.Build.props` gives the API. That is the whole point: the version has to describe
 * the code executing right now, so comparing it with the server's answer detects a bundle the
 * browser is serving out of its own cache. A number fetched at runtime would describe the server
 * instead, and could never detect anything.
 */
declare const __CRM_VERSION__: string;

/** "1.1.0.3" - release then build number. All four components, always. */
export const APP_VERSION: string = __CRM_VERSION__;

/** What the sidebar shows beside the product name. */
export const VERSION_LABEL = APP_VERSION.length === 0 ? '' : `v${APP_VERSION}`;

/**
 * Whether `served` is a build this browser should reload to get.
 *
 * Deliberately "different", not "higher". A rollback is a deployment too, and a browser holding the
 * version that was just withdrawn is exactly as stale as one holding an older one - telling it to
 * sit on a build the server no longer serves would be the wrong answer. An empty or unreadable
 * answer means the server did not say, which is never a reason to prompt.
 */
export function isStale(served: string | null | undefined): boolean {
  if (!served || !served.trim() || APP_VERSION.length === 0) return false;
  return served.trim().toLowerCase() !== APP_VERSION.toLowerCase();
}

/**
 * Reloads onto the build the server is serving.
 *
 * Has to defeat the very cache that caused the problem, so it drops any Cache Storage and service
 * worker first - belt and braces, since this app registers neither today, but a PWA shell later
 * would - and then reloads with a changed query so the document itself cannot come from cache. The
 * bundle beneath it is content-stamped, so a fresh document pulls the new one by name.
 *
 * The hash is cleared on the way: a stale client is stale in ways it cannot know about, and landing
 * back on a deep link the new build may have moved is a worse first impression than starting over.
 */
export async function reloadForUpdate(): Promise<void> {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    }
  } catch {
    /* best effort - a refused cache API must not stop the reload */
  }
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations?.();
    await Promise.all((registrations ?? []).map(r => r.unregister()));
  } catch {
    /* as above */
  }

  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('updated', Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Puts the product and the running build in the browser tab.
 *
 * The tab title is what ends up in a screenshot and in the window list, so it is the version a
 * support conversation gets handed without anyone having to be asked for it. Unlike the OpenSilver
 * app this needs stamping only once - there is no navigation journal here to overwrite it.
 */
export function stampDocumentTitle(unseenTickets = 0): void {
  const base = VERSION_LABEL.length === 0 ? 'Jetsonsoft CRM' : `Jetsonsoft CRM ${VERSION_LABEL}`;
  // A count up front is what shows in a background tab, which is where the CRM usually is when a
  // customer raises a ticket.
  document.title = unseenTickets > 0 ? `(${unseenTickets}) ${base}` : base;
}
